import type { Timestamps, SoftDeletable, PaginationParams, SortParams } from "./common";

// ─── Enums ────────────────────────────────────────────────────────────────────

export type ProductCategory =
  | "bebida"
  | "alimento"
  | "insumo"
  | "empaque"
  | "limpieza"
  | "otro";

export type InventoryMovementType =
  | "entrada"   // Manual stock addition
  | "salida"    // Manual stock removal
  | "pedido"    // Auto-deducted by order (status → entregado)
  | "ajuste"    // Correction — includes inventory reversal on cancellation
  | "merma";    // Waste/loss

// ─── Alert level ─────────────────────────────────────────────────────────────
// rojo:     cantidad_actual < cantidad_minima
// amarillo: cantidad_actual < cantidad_minima * 1.20  (within 20% above minimum)
// ok:       cantidad_actual >= cantidad_minima * 1.20

export type AlertLevel = "ok" | "amarillo" | "rojo";

export function calcularAlerta(actual: number, minimo: number): AlertLevel {
  if (actual < minimo)            return "rojo";
  if (actual < minimo * 1.20)     return "amarillo";
  return "ok";
}

// ─── Product ──────────────────────────────────────────────────────────────────

export interface Product extends Timestamps, SoftDeletable {
  id: number;
  nombre: string;
  descripcion: string | null;
  categoria: ProductCategory;
  precio_venta: number;
  unidad: string;
  activo: boolean;
}

// ─── Inventory (current stock per product) ───────────────────────────────────

export interface Inventory extends Timestamps {
  id: number;
  producto_id: number;
  cantidad_actual: number;
  cantidad_minima: number;
  cantidad_maxima: number | null;
  producto?: Product;
  // Computed
  alerta?: AlertLevel;
  porcentaje_minimo?: number;  // (cantidad_actual / cantidad_minima) * 100
}

// ─── Receta (bill of materials) ───────────────────────────────────────────────

export interface Receta extends Timestamps {
  id: number;
  producto_id: number;
  insumo_id: number;
  cantidad: number;
  unidad: string;
  // Joined (read-only)
  producto_nombre?: string;
  insumo_nombre?: string;
}

// ─── Inventory movement log ───────────────────────────────────────────────────

export interface InventoryLog extends Timestamps {
  id: number;
  producto_id: number;
  tipo: InventoryMovementType;
  cantidad: number;            // Positive = in, negative = out
  cantidad_anterior: number;
  cantidad_nueva: number;
  referencia_id: number | null;
  referencia_tipo: string | null;
  notas: string | null;
  creado_por: string;
  producto_nombre?: string;
}

// ─── Alert item ───────────────────────────────────────────────────────────────

export interface InventoryAlert {
  producto_id: number;
  producto_nombre: string;
  unidad: string;
  cantidad_actual: number;
  cantidad_minima: number;
  nivel: AlertLevel;
  porcentaje: number;         // (cantidad_actual / cantidad_minima) * 100
}

// ─── Request/Response types ───────────────────────────────────────────────────

export interface ProductCreate {
  nombre: string;
  descripcion?: string;
  categoria: ProductCategory;
  precio_venta: number;
  unidad: string;
  activo?: boolean;
}

export interface ProductUpdate extends Partial<ProductCreate> {}

export interface InventoryAdjust {
  producto_id: number;
  tipo: Extract<InventoryMovementType, "entrada" | "salida" | "ajuste" | "merma">;
  cantidad: number;   // Always positive; direction inferred from tipo
  notas?: string;
}

export interface InventoryFilters extends PaginationParams, SortParams {
  categoria?: ProductCategory;
  solo_alertas?: boolean;       // only amarillo + rojo
  solo_bajo_minimo?: boolean;   // only rojo
  search?: string;
  activo?: boolean;
}
